import { useCallback, useEffect, useRef, useState } from 'react';

import vtkAnnotatedCubeActor, {
  IStyle,
} from '@kitware/vtk.js/Rendering/Core/AnnotatedCubeActor';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';

import {
  axesLineRenderer,
  getCArmAngles,
  setCameraByCArmAngles,
  SurfaceContext,
  updateRenderWindow,
  updateResetCamera,
  useBufferPool,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useConfig } from 'config/useConfig';
import useModalActions from '@hooks/common/useModalAction';
import useAuth from '@hooks/Auth/useAuth';

import { initCameraForAnalysis as initCameraVR } from '@features/view/VRUtilities';

import {
  initCameraForAnalysis as initCameraSurface,
  getWallthicknessValue,
  findClosestScalarId,
  createFFRCenterLine,
} from '@features/view/Surface3DUtilities';

import { useBranchSelectionHandler } from '@features/view/useBranchSelectionManager';
import { run_myocardial } from '@components/MyoCardialUtilities/MyoCardialUtilities';
import { hexToNormalizedRGB } from 'config/colorUtils';
import { setAxesLinesVisibility } from '@features/view/axesLines';
import { useSettingsModal } from '@features/analysis/analysisModal/SettingsModal/useSettingsModal';
import CArmAnglesModal from '@features/analysis/rendering/Panels/Renderers/CArmAnglesModal';
import { Vector3 } from '@kitware/vtk.js/types';
import { useMeasurementOverlayState } from './useMeasurementOverlay';
import {
  clearMeasurementActors,
  CPRNodeRenderer,
  setActorOpacity,
  setActorVisible,
  taviNodeAllRenderer,
} from '@features/view/Shared3DUtilities';
import { FFR_OPACITY, FFRNames, nodeNames } from 'config/render3DConfig';
import { getVolumes } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { EditNames } from 'config/analysisConfig';

type renderArgs = {
  context: React.MutableRefObject<VRContext | SurfaceContext | null>;
  lowQuality: boolean;
};

export function use3DRendererManager({ context, lowQuality }: renderArgs) {
  const { selectedSeriesId, seriesSelections } = useBrowserPage();
  const { analysisType, selectedPanelComponentKey } = usePanelContainer();

  const { bufferPool, setBufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const [wallThicknessValue, setWallThicknessValue] = useState<number>(0);
  const { waitingAction, closeLoadingModal } = useModalActions();
  const { accessToken } = useAuth();
  const resetNodeRef = useRef<boolean>(false);

  const { config, getColors } = useConfig();
  const [CArmAngles, setCArmAngles] =
    useState<ReturnType<typeof getCArmAngles>>();
  const { general } = useSettingsModal();
  const {
    measurementPointsRef,
    measurementSvgRef,
    measurementMode,
    measurementLabelItems,
    updateMeasurementLabelPositions,
    recordMeasurementHistory,
    renderMeasurementLine,
    undoMeasurement,
    redoMeasurement,
    clearMeasurementHistory,
  } = useMeasurementOverlayState({ context, lowQuality });

  const handleOpenCArmAnglesModal = (onClose: () => void) => (
    <CArmAnglesModal
      initialPrimary={CArmAngles?.primary}
      initialSecondary={CArmAngles?.secondary}
      onClose={() => onClose()}
      onSubmit={({ primary, secondary, roll }) => {
        //각도를 적용했을때 3D model 이동,회전 다 리셋시키고 진행하는걸로 해볼까?
        updateResetCamera(context, analysisType,pool);
        const success = setCameraByCArmAngles(
          context,
          primary,
          secondary,
          roll,
        );
        if (success) {
          handleUpdateRenderWindow('');
        }
        //onClose();
      }}
    />
  );

  useEffect(() => {
    updateAxesStyle();
  }, [general.font]);

  // useEffect(() => {
  //   if (!context.current) return;
  //   context.current.measurementHistory = {
  //     undoStack: [],
  //     redoStack: [],
  //   };
  // }, [selectedSeriesId]);

  const { getSelectedBranchIdAndNodeIndex, handleBranchSelection } =
    useBranchSelectionHandler({
      context,
      lowQuality,
    });

  const colorMap = [
    hexToNormalizedRGB(config.colors.Proximal),
    hexToNormalizedRGB(config.colors.Stenosis),
    hexToNormalizedRGB(config.colors.Distal),
  ];

  function applyViewFocus(
    name: string,
    world: Vector3,
    prevFocus: Vector3,
  ): Vector3 {
    if (!name || !prevFocus) return world;
    switch (name) {
      case 'Axial':
        // Z는 현재 world 유지, X/Y는 이전 focus 유지
        return [prevFocus[0], prevFocus[1], world[2]];
      case 'Coronal':
        // Y는 현재 world 유지, X/Z는 이전 focus 유지
        return [prevFocus[0], world[1], prevFocus[2]];
      case 'Sagittal':
        // X는 현재 world 유지, Y/Z는 이전 focus 유지
        return [world[0], prevFocus[1], prevFocus[2]];
      default:
        return world;
    }
  }

  function center3DCameraOnFocus(world: Vector3) {
    const current = context.current;
    if (!current?.renderer) return;

    const camera = current.renderer.getActiveCamera?.();
    if (!camera) return;

    const nextFocalPoint = [-world[0], world[1], world[2]] as Vector3;
    const previousFocalPoint = camera.getFocalPoint?.() as Vector3 | undefined;
    const previousPosition = camera.getPosition?.() as Vector3 | undefined;
    if (!previousFocalPoint || !previousPosition) return;

    const delta = [
      nextFocalPoint[0] - previousFocalPoint[0],
      nextFocalPoint[1] - previousFocalPoint[1],
      nextFocalPoint[2] - previousFocalPoint[2],
    ] as Vector3;

    camera.setFocalPoint(...nextFocalPoint);
    camera.setPosition(
      previousPosition[0] + delta[0],
      previousPosition[1] + delta[1],
      previousPosition[2] + delta[2],
    );
    current.renderer.resetCameraClippingRange?.();
    updateRenderWindow(context);
  }

  function handleFocus(args: any) {
    let world = args.world as [number, number, number];
    if (!context.current || !world || !pool || !selectedSeriesId) return;
    if(analysisType === 'FFR' && !config.EditMode) return;

    if (context.current.focus) {
      const prevFocus = context.current.focus as Vector3;
      world = applyViewFocus(args.name, args.world, prevFocus);
    }
    context.current.focus = world;
    center3DCameraOnFocus(world as Vector3);
    if (measurementMode) {
      if (measurementPointsRef.current.length === 0) {
        measurementPointsRef.current.push([]);
      }
      const activePoints =
        measurementPointsRef.current[measurementPointsRef.current.length - 1];
      recordMeasurementHistory();
      activePoints.push([world[0], world[1], world[2]]);
      if (activePoints.length >= 2) {
        renderMeasurementLine(
          activePoints,
          measurementPointsRef.current.length - 1,
        );
      }
    }

    const isAxisFixed = ['myocardial','FFR'].includes(analysisType);
    if (!isAxisFixed || config.EditMode === 'regulator' || config.EditMode === 'lumen') {
      context.current.savedTranslation = [-world[0], world[1], world[2]];
    }
    if (analysisType === 'wallthickness') {
      let cellId = args.cellId;
      // if (args.mode !== 'mouse') {
      //   const res = pickFromWorld(context, [-world[0], world[1], world[2]]);
      //   if (res) {
      //     context.current.savedTranslation = res.worldPosition;
      //     context.current.focus = [
      //       -res.worldPosition[0],
      //       res.worldPosition[1],
      //       res.worldPosition[2],
      //     ];
      //     cellId = res.cellId;
      //   }
      // }
      const res = findClosestScalarId(
        context as React.MutableRefObject<SurfaceContext | null>,
        'laWall',
        args.world,
      );
      if (res?.pointId && res.minDist < 5) {
        const id = getWallthicknessValue(
          context as React.MutableRefObject<SurfaceContext | null>,
          selectedSeriesId!,
          pool!,
          res?.pointId,
        );
        setWallThicknessValue(id!);
      } else {
        setWallThicknessValue(0);
      }
    }


    if (args.mode === 'cpr') {
      handleBranchSelection(world);
    } else if (args.mode === 'mouse') {
      const isCoronaryPick = ['calcium', 'coronary'].includes(args.pickId);
      const isVR = context.current.viewportId === 'VR';
      const shouldSelectBranch =
        (['plaque', 'isr', 'stenosis'].includes(analysisType) &&
          (isVR || isCoronaryPick)) ||
        (analysisType === 'FFR' && config.EditMode === EditNames.lumen);
      if (shouldSelectBranch) {
        handleBranchSelection(world);
      }

      if (isCoronaryPick && analysisType === 'myocardial') {
        const run = async () => {
          const selected = getSelectedBranchIdAndNodeIndex(world);
          if (!selected) return;

          const [branchid, index] = selected;

          waitingAction({ message: 'Waiting' });

          try {
            await run_myocardial(accessToken, selectedSeriesId!, {
              branchid,
              input_idx: index,
            });
          } finally {
            closeLoadingModal();
          }
        };
        run();
      }
    }
    axesLineRenderer(context);
  }

  function handleMarkerMove(args: any) {
    const { type } = args;
    if (!context.current) return;
    if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi'){
      updateRenderWindow(context);
      return;
    }
    const { newPickedPoints: branchState, which, mainIndex } = args;
    if (args.update3d === false && type === 'polygonMove') {
      resetNodeRef.current = true;
      updateRenderWindow(context);
      return;
    }
    if (!branchState || branchState.length === 0) return;
    const updateColor: [number, number, number][] =
      which === 'total' ? colorMap : [colorMap[which]];
    CPRNodeRenderer(context, mainIndex, branchState, which, updateColor, true);
  }

  function handleRotate(args: any) {
    if (!context.current) return;
    const { xAxis: x, yAxis: y, zAxis: z } = args;
    const pos = {
      x: [-x[0], x[1], x[2]],
      y: [-y[0], y[1], y[2]],
      z: [-z[0], z[1], z[2]],
    };
    if (selectedPanelComponentKey === 'Axial') {
      context.current.axes.x = pos.x as [number, number, number];
      context.current.axes.y = pos.y as [number, number, number];
    } else if (selectedPanelComponentKey === 'Coronal') {
      context.current.axes.x = pos.x as [number, number, number];
      context.current.axes.z = pos.z as [number, number, number];
    } else if (selectedPanelComponentKey === 'Sagittal') {
      context.current.axes.y = pos.y as [number, number, number];
      context.current.axes.z = pos.z as [number, number, number];
    }
    axesLineRenderer(context);
  }

  function handleAxes(args: any) {
    if (!context.current) return;
    setAxesLinesVisibility({
      context,
      visible: args.visible,
    });
    context.current.isAxis3DVisible = args.visible;
    updateRenderWindow(context);
  }

  function handleUpdateRenderWindow(args: any) {
    if (!context.current) return;
    setCArmAngles(getCArmAngles(context));
    updateRenderWindow(context);
  }

  function handleReset(args: any, toggleAxes: boolean) {
    if (!context.current || !pool) return;

    if (context.current.viewportId === 'VR') {
      initCameraVR(
        context as React.MutableRefObject<VRContext | null>,
        analysisType,
        selectedSeriesId!,
        pool,
      );
    } else {
      initCameraSurface(
        context as React.MutableRefObject<SurfaceContext | null>,
        analysisType,
        selectedSeriesId!,
        pool,
      );
    }

    if (analysisType === 'tavi' && selectedSeriesId) {
      //원래대로
      taviNodeAllRenderer(context, pool, selectedSeriesId);
    }

    clearMeasurementHistory();
    clearMeasurementActors(context);

    context.current.isAxis3DVisible = toggleAxes;
  }

  function updateFFRCentline(branchId: number) {
    if (!context.current) return;
    setActorOpacity(
      context,
      [
        FFRNames.main_lca,
        FFRNames.main_rca,
        FFRNames.cap_lca,
        FFRNames.cap_rca,
      ],
      FFR_OPACITY.DEFAULT,
    );

    createFFRCenterLine(
      context as React.MutableRefObject<SurfaceContext | null>,
      pool!,
      selectedSeriesId!,
      analysisType,
      branchId,
    );
  }

  function updateAxesStyle() {
    const orientationWidget = context.current?.orientationWidget;
    if (!orientationWidget) return;
    const currentFont = general.font;

    const axes = orientationWidget.getActor() as vtkAnnotatedCubeActor;
    axes.setDefaultStyle({
      fontFamily: currentFont,
    });
    axes.modified();

    orientationWidget.modified();
    context.current?.renderWindow.render();
  }

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    e.preventDefault();
    if (e.ctrlKey && e.key.toLowerCase() === 'z') {
      undoMeasurement();
      return;
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'y') {
      redoMeasurement();
      return;
    }
    if (e.code === 'F2') {
    if (!context.current) return;
      console.log(context.current);
      console.log(pool);
      console.log(context.current.renderer.getActors());
      getVolumes();
      return;
    }
  }, [selectedSeriesId,pool]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    CArmAngles,
    wallThicknessValue,
    resetNodeRef,
    handleFocus,
    handleMarkerMove,
    handleRotate,
    handleAxes,
    handleUpdateRenderWindow,
    handleReset,
    handleOpenCArmAnglesModal,
    measurementSvgRef,
    measurementLabelItems,
    updateMeasurementLabelPositions,
    updateFFRCentline,
  };
}
